import { useRouter } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { playerFrame } from '@realmery/realm-core';
import { realms } from '../db/client.ts';
import { PLAYER_CHANNEL, isHello, type ToPlayerView } from './playerChannel.ts';

/**
 * The GM's side of the Player View (docs/PARITY-STEP-4-PLAN.md section 9), for the realm
 * that's open: which map players are shown, opening and closing the Player View's window,
 * and sending it that map as players may see it - again after every change the GM saves,
 * wherever in the realm they are. It's sent only what `playerFrame` lets through.
 */

interface PlayerView {
  /** The map being shown to players, or null. */
  readonly mapId: string | null;
  show(mapId: string): void;
  close(): void;
}

const Context = createContext<PlayerView>({ mapId: null, show: () => undefined, close: () => undefined });

export const usePlayerView = () => useContext(Context);

export function PlayerViewHost({ file, children }: { file: string; children: ReactNode }) {
  const router = useRouter();
  const [mapId, setMapId] = useState<string | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);
  /** The picture last sent, so it goes again only when it changes or the Player View asks. */
  const sentImage = useRef<string | null>(null);
  const shown = useRef<string | null>(null);
  shown.current = mapId;

  const post = (message: ToPlayerView) => channel.current?.postMessage(message);

  const send = useCallback(
    async (withImage: boolean) => {
      const id = shown.current;
      if (!id) return post({ type: 'closed' });
      const view = await realms.atlasMap(file, id);
      if (shown.current !== id) return;
      if (!view) {
        setMapId(null);
        return post({ type: 'closed' });
      }
      const frame = playerFrame({ map: view.map, markers: view.markers, regions: view.regions, categories: view.categories, icons: view.icons, layers: view.layers, drawings: view.drawings });
      if (withImage || sentImage.current !== frame.imageKey) {
        post({ type: 'image', key: frame.imageKey, mimeType: view.map.mimeType, data: view.map.imageData });
        sentImage.current = frame.imageKey;
      }
      post({ type: 'frame', frame });
    },
    [file],
  );

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const c = new BroadcastChannel(PLAYER_CHANNEL);
    channel.current = c;
    // A Player View opening (or reloading) asks for the map.
    c.onmessage = (event) => {
      if (isHello(event.data)) void send(true);
    };
    return () => {
      // Leaving the realm closes what players see, as on the desktop.
      c.postMessage({ type: 'closed' } satisfies ToPlayerView);
      c.close();
      channel.current = null;
    };
  }, [send]);

  // Every change saved anywhere in the realm reloads the page's data: the shown map is sent again.
  useEffect(() => router.subscribe('onResolved', () => void send(false)), [router, send]);
  useEffect(() => void send(true), [mapId, send]);

  const value: PlayerView = {
    mapId,
    show(id) {
      setMapId(id);
      // The window for a TV or second screen; one, reused if it's already open.
      window.open('#/player', 'realmery-player-view', 'popup,width=1100,height=760');
    },
    close() {
      setMapId(null);
      sentImage.current = null;
    },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
